import { useEffect, useState } from "react";
import {
    fetchAdminFinalists,
    fetchCategories,
    setFinalists,
    type Category,
} from "../api/tcg";

const EMPTY_FINALISTS = ["", "", "", "", ""];

function AdminActions({ token }: { token: string }) {
    const [categories, setCategories] = useState<Category[]>([]);
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [finalists, setFinalistsState] = useState<string[]>(EMPTY_FINALISTS);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetchCategories()
            .then((cats) => {
                const active = cats.filter((c) => c.group_key === "product" || c.group_key === "community");
                setCategories(active);
                if (active.length > 0) setCategoryId(active[0].id);
            })
            .catch((err) => setError(err instanceof Error ? err.message : "Fehler."));
    }, []);

    // Bei Kategoriewechsel: bereits gespeicherte Finalisten laden, damit man
    // nicht versehentlich in ein leeres Formular hinein überschreibt.
    useEffect(() => {
        if (categoryId === null) return;

        setMessage(null);
        setError(null);

        fetchAdminFinalists(token, categoryId)
            .then((rows) => {
                const next = [...EMPTY_FINALISTS];
                for (const row of rows) {
                    if (row.position >= 1 && row.position <= 5) {
                        next[row.position - 1] = row.display_text;
                    }
                }
                setFinalistsState(next);
            })
            .catch(() => setFinalistsState(EMPTY_FINALISTS));
    }, [categoryId, token]);

    function updateFinalist(index: number, value: string) {
        setFinalistsState((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    }

    async function handleSetFinalists() {
        if (categoryId === null) return;
        const trimmedFinalists = finalists.map((f) => f.trim());
        const normalizedFinalists = trimmedFinalists.map((f) => f.toLowerCase());
        const uniqueFinalists = new Set(normalizedFinalists);

        if (trimmedFinalists.some((f) => f === "")) {
            setMessage(null);
            setError("Bitte fülle alle 5 Finalisten-Felder aus.");
            return;
        }

        if (uniqueFinalists.size !== trimmedFinalists.length) {
            setMessage(null);
            setError("Bitte vermeide doppelte Finalisten.");
            return;
        }

        setBusy(true);
        setMessage(null);
        setError(null);
        try {
            await setFinalists(token, categoryId, trimmedFinalists);
            setMessage("Finalisten gespeichert.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <section className="admin-card">
            <h2 className="admin-card-title">Finalisten der Votingphase</h2>

            <div className="admin-control-row">
                <label className="admin-label">Kategorie</label>
                <select
                    className="admin-input"
                    value={categoryId ?? ""}
                    onChange={(e) => setCategoryId(Number(e.target.value))}
                >
                    {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </div>

            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}

            <div className="admin-action-block">
                <h3 className="admin-subtitle">5 Finalisten der Votingphase</h3>
                <p className="admin-note">
                    Beim Start der Votingphase werden die Top 5 der bereinigten Nominierungen
                    automatisch übernommen. Hier können die gesetzten Finalisten geprüft und
                    bei Bedarf manuell korrigiert werden.
                </p>
                <p className="admin-note" style={{ marginTop: "8px", marginBottom: "16px" }}>
                    Änderungen werden erst durch „Speichern“ übernommen.
                    Bereits gespeicherte Finalisten werden nicht automatisch überschrieben.
                </p>

                {finalists.map((f, i) => (
                    <input
                        key={i}
                        className="admin-input"
                        type="text"
                        placeholder={`Finalist ${i + 1}`}
                        value={f}
                        onChange={(e) => updateFinalist(i, e.target.value)}
                    />
                ))}
                <button className="admin-btn-primary" onClick={handleSetFinalists} disabled={busy}>
                    Speichern
                </button>
            </div>
        </section>
    );
}

export default AdminActions;
